import React from 'react';
import { Select as MUISelect, FormControl, InputLabel, MenuItem } from '@material-ui/core';
import { SelectProps as MUISelectProps } from '@material-ui/core/Select';
import { useSelectStyles } from 'components/UI/V2/Forms/Select/Select.styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';
import { Label } from 'components/UI/V2/Forms/Label/Label';

export interface SelectOption<T> {
    label: string;
    value: string;
    data?: T;
}

export interface SelectProps<T> {
    label?: string;
    icon?: IconProp;
    error?: string;
    placeholder?: string;
    wrapperClassName?: string;
    options: Array<SelectOption<T>>;
    selectProps?: MUISelectProps;
    renderOption?: (option: SelectOption<T>) => React.ReactNode;
    value?: MUISelectProps['value'];
    onChange?: MUISelectProps['onChange'];
}

const SelectIcon: React.FunctionComponent<{ icon?: IconProp; className?: string }> = props => {
    return (
        <FontAwesomeIcon
            icon={props.icon || faChevronDown}
            className={clsx('MuiSvgIcon-root', 'MuiSelect-icon', 'MuiSelect-iconOutlined', props.className)}
        />
    );
};

export const Select = <T,>({ options, wrapperClassName, renderOption, ...props }: SelectProps<T>) => {
    const classes = useSelectStyles(props as SelectProps<unknown>);

    return (
        <div className={classes.root}>
            {props.label && <Label error={props.error}>{props.label}</Label>}

            <FormControl
                fullWidth
                className={clsx(classes.select, wrapperClassName)}
            >
                <InputLabel shrink={false}>{props.placeholder}</InputLabel>

                <MUISelect
                    {...props.selectProps}
                    value={props.value}
                    MenuProps={{
                        style: { maxHeight: 300, zIndex: 20000 },
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left'
                        },
                        transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left'
                        },
                        getContentAnchorEl: null
                    }}
                    variant={'outlined'}
                    IconComponent={() => SelectIcon({ icon: props.icon, className: classes.icon })}
                    onChange={props.onChange}
                >
                    {options.map(option => (
                        <MenuItem
                            key={option.value}
                            value={option.value}
                        >
                            {renderOption ? renderOption(option) : option.label}
                        </MenuItem>
                    ))}
                </MUISelect>
            </FormControl>
        </div>
    );
};
